import React, { createContext, useContext, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";

const TutorialContext = createContext();

const TUTORIAL_STEPS = {
  dashboard: [
    {
      title: "Welcome to Dashboard",
      description:
        "This is your main dashboard where you can access all tax notice management tools.",
      target: () => document.querySelector('[data-tutorial="main-dashboard"]'),
      placement: "bottom",
    },
    {
      title: "GST Management",
      description: "Click here to manage your GST notices efficiently.",
      target: () => document.querySelector('[data-tutorial="gst-card"]'),
      placement: "top",
    },
    {
      title: "Income Tax",
      description: "Handle all your income tax notices from this section.",
      target: () => document.querySelector('[data-tutorial="income-tax-card"]'),
      placement: "top",
    },
    {
      title: "TDS/TCS",
      description: "Manage TDS and TCS related notices here.",
      target: () => document.querySelector('[data-tutorial="tds-card"]'),
      placement: "top",
    },
  ],
  gstin: [
    {
      title: "GST Dashboard Overview",
      description:
        "Welcome to your GST management hub. This is where you'll handle all GST-related notices and client management.",
      target: () => document.querySelector('[data-tutorial="gst-main"]'),
      placement: "bottom",
    },
    {
      title: "Notice Statistics",
      description:
        "These cards show your GST notice statistics - total notices, recent activity, open notices, and overdue items.",
      target: () => document.querySelector('[data-tutorial="notice-cards"]'),
      placement: "right",
    },
    {
      title: "Total Notices",
      description:
        "View all your GST notices here. Click to see the complete list.",
      target: () => document.querySelector('[data-tutorial="total-notices"]'),
      placement: "top",
    },
    {
      title: "Recent Activity",
      description:
        "Track notices from the last 24 hours to stay updated with recent developments.",
      target: () => document.querySelector('[data-tutorial="recent-notices"]'),
      placement: "top",
    },
    {
      title: "Calendar View",
      description:
        "Your GST calendar helps track important dates, deadlines, and scheduled tasks.",
      target: () => document.querySelector('[data-tutorial="gst-calendar"]'),
      placement: "left",
    },
    {
      title: "Client Management",
      description:
        "These buttons help you manage your GST clients - add new clients, import existing data, or edit client information.",
      target: () => document.querySelector('[data-tutorial="client-buttons"]'),
      placement: "bottom",
    },
    {
      title: "Add New Client",
      description: "Click here to add a new GST client to your system.",
      target: () => document.querySelector('[data-tutorial="add-client"]'),
      placement: "bottom",
    },
    {
      title: "Import Clients",
      description:
        "Bulk import client data from external sources like Excel or CSV files.",
      target: () => document.querySelector('[data-tutorial="import-client"]'),
      placement: "bottom",
    },
    {
      title: "Edit Clients",
      description:
        "Modify existing client information and manage client profiles.",
      target: () => document.querySelector('[data-tutorial="edit-client"]'),
      placement: "bottom",
    },
  ],
  "income-tax": [
    {
      title: "Income Tax Dashboard",
      description: "Your income tax notice management hub.",
      target: () => document.querySelector('[data-tutorial="it-dashboard"]'),
      placement: "bottom",
    },
  ],
  tds: [
    {
      title: "TDS Management",
      description: "Comprehensive TDS notice handling tools.",
      target: () => document.querySelector('[data-tutorial="tds-dashboard"]'),
      placement: "bottom",
    },
  ],
};

export const TutorialProvider = ({ children }) => {
  const [isFirstUser, setIsFirstUser] = useState(false);
  const [showWelcomeModal, setShowWelcomeModal] = useState(false);
  const [currentTour, setCurrentTour] = useState(null);
  const [tourOpen, setTourOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState("dashboard");
  const [tutorialProgress, setTutorialProgress] = useState({});
  const [pendingNavigation, setPendingNavigation] = useState(null);

  useEffect(() => {
    const isFirst =
      localStorage.getItem("isFirstPurchase") === "1" ||
      !localStorage.getItem("tutorialCompleted");
    setIsFirstUser(isFirst);

    if (isFirst && !localStorage.getItem("welcomeModalShown")) {
      setShowWelcomeModal(true);
    }
  }, []);

  const startTutorial = (page = currentPage, navigate = null) => {
    const steps = TUTORIAL_STEPS[page];
    if (steps) {
      // If we need to navigate to a different page for the tutorial
      if (page === "dashboard" && navigate) {
        // Start with dashboard tour, then navigate to GST
        setCurrentTour(steps);
        setTourOpen(true);
        setShowWelcomeModal(false);
        setPendingNavigation({ page: "gstin", navigate });
      } else {
        setCurrentTour(steps);
        setTourOpen(true);
        setShowWelcomeModal(false);
        setCurrentPage(page);
      }
    }
  };

  // Function to handle navigation between tutorial sections
  const navigateToNextSection = (navigate) => {
    if (pendingNavigation) {
      const { page, navigate: navFunction } = pendingNavigation;
      setTourOpen(false);

      // Navigate to GST page
      navFunction("/gstin");

      // Wait a moment for navigation to complete, then start GST tutorial
      setTimeout(() => {
        startTutorialForPage(page);
      }, 500);

      setPendingNavigation(null);
    }
  };

  // Start tutorial for a specific page without navigation logic
  const startTutorialForPage = (page) => {
    const steps = TUTORIAL_STEPS[page];
    if (steps) {
      setCurrentTour(steps);
      setTourOpen(true);
      setCurrentPage(page);
    }
  };

  // Skip tutorial
  const skipTutorial = () => {
    setShowWelcomeModal(false);
    localStorage.setItem("welcomeModalShown", "true");
  };

  // Complete tutorial
  const completeTutorial = (page) => {
    setTourOpen(false);
    setTutorialProgress((prev) => ({ ...prev, [page]: true }));

    // If there's pending navigation, handle it
    if (pendingNavigation) {
      navigateToNextSection();
      return;
    }

    const allPages = Object.keys(TUTORIAL_STEPS);
    const completed = allPages.every((p) => tutorialProgress[p] || p === page);

    if (completed) {
      localStorage.setItem("tutorialCompleted", "true");
      setIsFirstUser(false);
    }
  };

  const setPage = (page) => {
    setCurrentPage(page);
  };

  const startGSTTutorial = (navigate) => {
    startTutorial("dashboard", navigate);
  };

  const value = {
    isFirstUser,
    showWelcomeModal,
    setShowWelcomeModal,
    startTutorial,
    skipTutorial,
    currentTour,
    tourOpen,
    setTourOpen,
    completeTutorial,
    currentPage,
    setPage,
    tutorialProgress,
    startTutorialForPage,
    startGSTTutorial,
    navigateToNextSection,
  };

  return (
    <TutorialContext.Provider value={value}>
      {children}
    </TutorialContext.Provider>
  );
};

export const useTutorial = () => {
  const context = useContext(TutorialContext);
  if (!context) {
    throw new Error("useTutorial must be used within TutorialProvider");
  }
  return context;
};
